import type {
  CockpitDataset,
  ImportRunDto,
  Me,
  OpsAchats,
  OpsCommentDto,
  OpsCommentInput,
  OpsDataset,
  OpsEcarts,
  OpsOrderLine,
  OpsPrev,
  PlanDataset,
  SyncRunDto,
  TraitementDto,
  UserDto,
} from '@cockpit/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './api.js';

export interface PlanResponse {
  dataset: PlanDataset;
  traitements: TraitementDto[];
  source: string;
  syncedAt: string;
}

export function useMe() {
  return useQuery<Me | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api<Me>('/api/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
  });
}

export const usePlan = () => useQuery({ queryKey: ['plan'], queryFn: () => api<PlanResponse>('/api/plan') });

export const useCockpit = () =>
  useQuery({ queryKey: ['cockpit'], queryFn: () => api<CockpitDataset>('/api/cockpit') });

export function useTraitement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, done }: { code: string; done: boolean }) =>
      api<TraitementDto>(`/api/plan/${encodeURIComponent(code)}/traitement`, {
        method: 'PUT',
        body: { done },
      }),
    onMutate: async ({ code, done }) => {
      await qc.cancelQueries({ queryKey: ['plan'] });
      const prev = qc.getQueryData<PlanResponse>(['plan']);
      if (prev) {
        const rest = prev.traitements.filter((t) => t.code !== code);
        qc.setQueryData<PlanResponse>(['plan'], {
          ...prev,
          traitements: done
            ? [...rest, { code, by: 'Vous', at: new Date().toISOString(), note: null }]
            : rest,
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(['plan'], ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: ['plan'] }),
  });
}

export const useImports = () =>
  useQuery({
    queryKey: ['imports'],
    queryFn: () => api<ImportRunDto[]>('/api/admin/imports'),
    refetchInterval: 5000,
  });

export const useSyncRuns = () =>
  useQuery({
    queryKey: ['sync'],
    queryFn: () => api<{ configured: boolean; cron: string; runs: SyncRunDto[] }>('/api/admin/sync'),
    refetchInterval: 5000,
  });

export const useUsers = () =>
  useQuery({ queryKey: ['users'], queryFn: () => api<UserDto[]>('/api/admin/users') });

export interface OpsResponse {
  dataset: OpsDataset;
  /** Situation au dernier jour du mois précédent (null tant qu'elle n'a pas été synchronisée). */
  prev: OpsPrev | null;
  source: string;
  syncedAt: string;
}

export const useOps = () => useQuery({ queryKey: ['ops'], queryFn: () => api<OpsResponse>('/api/ops') });

const opsPath = (code: string, rest: string) => `/api/ops/${encodeURIComponent(code)}/${rest}`;

/** Lectures SYNC à la demande : chargées à l'ouverture de l'onglet, gardées 5 minutes. */
export const useOpsEcarts = (code: string, enabled: boolean) =>
  useQuery({
    queryKey: ['ops', code, 'ecarts'],
    queryFn: () => api<OpsEcarts>(opsPath(code, 'ecarts')),
    enabled,
    staleTime: 5 * 60_000,
  });

export const useOpsAchats = (code: string, enabled: boolean) =>
  useQuery({
    queryKey: ['ops', code, 'achats'],
    queryFn: () => api<OpsAchats>(opsPath(code, 'achats')),
    enabled,
    staleTime: 5 * 60_000,
  });

export const useOrderLines = (code: string, order: string, enabled: boolean) =>
  useQuery({
    queryKey: ['ops', code, 'commande', order],
    queryFn: () => api<OpsOrderLine[]>(opsPath(code, `commandes/${encodeURIComponent(order)}`)),
    enabled,
    staleTime: 5 * 60_000,
  });

export const useOpsComments = (code: string) =>
  useQuery({
    queryKey: ['ops', code, 'comments'],
    queryFn: () => api<OpsCommentDto[]>(opsPath(code, 'comments')),
  });

export function useAddComment(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OpsCommentInput) =>
      api<OpsCommentDto>(opsPath(code, 'comments'), { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ops', code, 'comments'] }),
  });
}

export function useActionDone(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) =>
      api<OpsCommentDto>(`/api/ops/comments/${encodeURIComponent(id)}`, { method: 'PATCH', body: { done } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ops', code, 'comments'] }),
  });
}
